/**
 * Visualization Feature Builder
 *
 * This is the main orchestrator for creating all map visualization features. It coordinates
 * between validation, feature creation, and error handling to build the complete set of
 * GeoJSON features that get rendered on the map.
 *
 * Key responsibilities:
 * - Orchestrate the complete visualization creation process
 * - Handle field boundary and usable area creation
 * - Coordinate plot validation and feature creation
 * - Manage trial boundaries and AB line features
 * - Provide unified error handling and result formatting
 * - Abstract complex logic away from React components
 *
 * This module acts as the "controller" that brings together:
 * - plotCalculations.ts (for AB line bearings)
 * - plotValidation.ts (for dimension validation)
 * - plotVisualization.ts (for feature creation)
 * - coordinateConversions.ts (for unit conversions)
 *
 * Used by: TestAreasVisualizer.tsx
 */

import * as turf from '@turf/turf';
import { getABLineBearing } from './plotCalculations';
import { validatePlotDimensions } from './plotValidation';
import { createReplicateFeatures } from './plotVisualization';
import {
  convertPlotDimensions,
  METERS_TO_DEGREES_LNG,
  METERS_TO_DEGREES_LAT,
  FEET_TO_METERS,
} from './coordinateConversions';
import { ValidationResult } from '../types/testAreasVisualizer.types';

export interface VisualizationParams {
  fieldBoundaryGeoJSON: any;
  trialBoundaryGeoJSON?: any;
  ablineGeoJSON?: any;
  plotWidthFt: number;
  plotLengthFt: number;
  bufferFt: number;
  plotBufferLengthFt: number;
  plotBufferWidthFt: number;
  isPlot: boolean;
  treatmentRates: string;
  nominalRate: number;
  applyTrialBufferToFieldBoundary?: boolean;
}

export interface VisualizationResult {
  features: any[];
  validation: ValidationResult;
  error?: string;
}

export function createVisualizationFeatures(
  params: VisualizationParams,
): VisualizationResult {
  const {
    fieldBoundaryGeoJSON,
    trialBoundaryGeoJSON,
    ablineGeoJSON,
    plotWidthFt,
    plotLengthFt,
    bufferFt,
    plotBufferLengthFt,
    plotBufferWidthFt,
    isPlot,
    treatmentRates,
    nominalRate,
    applyTrialBufferToFieldBoundary = true,
  } = params;

  try {
    // Check if we have valid field boundary data
    if (!fieldBoundaryGeoJSON?.features?.[0]) {
      return {
        features: [],
        validation: {
          plotWidthValid: true,
          plotLengthValid: true,
          bufferValid: true,
          plotBufferValid: true,
          plotBufferWidthValid: true,
          plotBufferLengthValid: true,
        },
      };
    }

    // Convert plot dimensions using consolidated utility
    const plotDimensions = convertPlotDimensions(plotWidthFt, plotLengthFt);
    const bufferM = bufferFt * FEET_TO_METERS;

    // Use standard coordinate conversion constants
    const metersToDegreesLng = METERS_TO_DEGREES_LNG;
    const metersToDegreesLat = METERS_TO_DEGREES_LAT;

    // Get AB line bearing if available

    const ablineBearing = getABLineBearing(ablineGeoJSON);

    const features = [];

    // 1. Add all field boundary features (white outline)
    fieldBoundaryGeoJSON.features.forEach((feature: any) => {
      const fieldBoundary = turf.clone(feature);
      fieldBoundary.properties = { type: 'fieldBoundary' };
      features.push(fieldBoundary);
    });

    // 2. Create usable area and buffer visualization from ALL field features
    const bufferResult = createUsableAreaAndBuffer(
      fieldBoundaryGeoJSON,
      trialBoundaryGeoJSON,
      bufferM,
      applyTrialBufferToFieldBoundary,
    );

    const { usableArea, bufferFeature, bufferFailed } = bufferResult;

    if (bufferFeature) {
      features.push(bufferFeature);
    }

    // Handle case where we have multiple buffer features that couldn't be unioned
    if (bufferResult.additionalBufferFeatures) {
      features.push(...bufferResult.additionalBufferFeatures);
    }

    // 3. Validate plot dimensions
    const validation = validatePlotDimensions(
      bufferFailed ? null : usableArea,
      plotDimensions.widthM,
      plotDimensions.lengthM,
      metersToDegreesLng,
      metersToDegreesLat,
      plotWidthFt,
      plotLengthFt,
      plotBufferWidthFt,
      plotBufferLengthFt,
      treatmentRates,
      nominalRate,
      bufferFt,
      ablineBearing, // Pass the actual AB line bearing to validation
      ablineGeoJSON, // Pass the AB line GeoJSON for smart placement
      isPlot, // Pass the isPlot flag to distinguish between plots and strips
    );

    // 4. Always try to create replicate features - let the positioning logic decide if they fit
    // This ensures that when validation clears, the replicate immediately appears
    if (usableArea && usableArea.geometry) {
      const replicateFeatures = createReplicateFeatures({
        usableArea,
        treatmentRates,
        nominalRate,
        plotWidthFt,
        plotLengthFt,
        plotBufferWidthFt,
        plotBufferLengthFt,
        isPlot,
        ablineBearing,
        ablineGeoJSON,
        metersToDegreesLng,
        metersToDegreesLat,
      });

      features.push(...replicateFeatures);
    }

    // 5. Add trial boundary if provided
    if (trialBoundaryGeoJSON?.features?.[0]) {
      const trialBoundary = turf.clone(trialBoundaryGeoJSON.features[0]);
      trialBoundary.properties = { type: 'trialBoundary' };
      features.push(trialBoundary);
    }

    // 6. Add AB line if provided
    if (ablineGeoJSON?.features?.[0]) {
      const abline = turf.clone(ablineGeoJSON.features[0]);
      abline.properties = { type: 'abline' };
      features.push(abline);
    }

    return { features, validation };
  } catch (err) {
    return {
      features: [],
      validation: {
        plotWidthValid: false,
        plotLengthValid: false,
        bufferValid: false,
        plotBufferValid: false,
        plotBufferWidthValid: false,
        plotBufferLengthValid: false,
      },
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

function createUsableAreaAndBuffer(
  fieldBoundaryGeoJSON: any,
  trialBoundaryGeoJSON: any,
  bufferM: number,
  applyTrialBufferToFieldBoundary: boolean = true,
): {
  usableArea: any;
  bufferFeature: any | null;
  bufferFailed: boolean;
  additionalBufferFeatures?: any[];
} {
  let usableArea;
  let bufferFailed = false;

  try {
    if (applyTrialBufferToFieldBoundary) {
      // Standard behavior: Buffer from field boundary, then constrain by trial boundary
      // Use the SAME simple logic as unchecked case, but start with field
      const fieldFeature = fieldBoundaryGeoJSON.features[0]; // Use first field feature for simplicity

      try {
        // Buffer field boundary inward
        usableArea = turf.buffer(fieldFeature, -bufferM / 1000, {
          units: 'kilometers',
        });

        // If trial boundary is provided, intersect with it to further constrain
        if (trialBoundaryGeoJSON?.features?.[0] && usableArea) {
          const trialBoundary = trialBoundaryGeoJSON.features[0];
          const intersection = turf.intersect(
            turf.featureCollection([usableArea, trialBoundary]),
          );

          if (intersection && intersection.geometry) {
            usableArea = intersection;
          }
          // If intersection fails, keep the buffered field boundary as usableArea
        }
        // If no trial boundary, usableArea is just the buffered field boundary
      } catch (err) {
        bufferFailed = true;
        usableArea = null;
      }
    } else {
      // Alternative behavior: Buffer around trial boundary instead of field boundary
      if (trialBoundaryGeoJSON?.features?.[0]) {
        // Use trial boundary and buffer inward from it
        const trialBoundary = trialBoundaryGeoJSON.features[0];
        try {
          usableArea = turf.buffer(trialBoundary, -bufferM / 1000, {
            units: 'kilometers',
          });

          // Still need to intersect with field boundary to ensure we don't go outside the field
          if (usableArea) {
            const allFieldFeatures = fieldBoundaryGeoJSON.features;
            for (const fieldFeature of allFieldFeatures) {
              try {
                const intersection: any = turf.intersect(
                  turf.featureCollection([usableArea, fieldFeature]),
                );
                if (intersection && intersection.geometry) {
                  usableArea = intersection;
                  break; // Use first successful intersection
                }
              } catch (err) {}
            }
          }
        } catch (err) {
          bufferFailed = true;
          usableArea = null;
        }
      } else {
        // No trial boundary provided, fall back to standard behavior
        usableArea = turf.buffer(
          fieldBoundaryGeoJSON.features[0],
          -bufferM / 1000,
          {
            units: 'kilometers',
          },
        );
      }
    }
  } catch (err) {
    bufferFailed = true;
    usableArea = null;
  }

  // Create buffer zone visualization using difference operation
  let bufferFeature = null;
  if (usableArea && usableArea.geometry) {
    try {
      const allBufferFeatures = [];

      if (applyTrialBufferToFieldBoundary) {
        // Standard behavior: Create buffer visualization for ALL field features
        for (const fieldFeature of fieldBoundaryGeoJSON.features) {
          try {
            // Create buffer for this specific field feature
            const fieldBuffered = turf.buffer(fieldFeature, -bufferM / 1000, {
              units: 'kilometers',
            });

            if (fieldBuffered && fieldBuffered.geometry) {
              const difference = turf.difference(
                turf.featureCollection([fieldFeature, fieldBuffered]),
              );

              if (difference && difference.geometry) {
                allBufferFeatures.push(difference);
              }
            }
          } catch (err) {
            // For individual field features that fail, use the original feature
            allBufferFeatures.push(turf.clone(fieldFeature));
          }
        }
      } else {
        // Alternative behavior: Create buffer visualization around trial boundary
        if (trialBoundaryGeoJSON?.features?.[0]) {
          try {
            const trialBoundary = trialBoundaryGeoJSON.features[0];
            const trialBuffered = turf.buffer(trialBoundary, -bufferM / 1000, {
              units: 'kilometers',
            });

            if (trialBuffered && trialBuffered.geometry) {
              const difference = turf.difference(
                turf.featureCollection([trialBoundary, trialBuffered]),
              );

              if (difference && difference.geometry) {
                allBufferFeatures.push(difference);
              }
            }
          } catch (err) {
            // Fallback: use the trial boundary itself
            allBufferFeatures.push(
              turf.clone(trialBoundaryGeoJSON.features[0]),
            );
          }
        }
      }

      // Combine all buffer features
      if (allBufferFeatures.length === 1) {
        bufferFeature = allBufferFeatures[0];
        bufferFeature.properties = { type: 'fieldBuffer' };
      } else if (allBufferFeatures.length > 1) {
        // Try to combine into a single feature if possible
        try {
          let combined = allBufferFeatures[0];
          for (let i = 1; i < allBufferFeatures.length; i++) {
            const unionResult = turf.union(
              turf.featureCollection([combined, allBufferFeatures[i]]),
            );
            if (unionResult) {
              combined = unionResult;
            }
          }
          combined.properties = { type: 'fieldBuffer' };
          bufferFeature = combined;
        } catch (err) {
          // If union fails, add each buffer feature individually to the main features array
          // We'll handle this in the main function by adding each buffer separately

          // Set bufferFeature to null so we can handle multiple features differently
          bufferFeature = null;

          // Add individual buffer features to the main features array
          allBufferFeatures.forEach((buffer) => {
            buffer.properties = { type: 'fieldBuffer' };
          });

          // Return a special object to indicate multiple features
          return {
            usableArea,
            bufferFeature: null,
            bufferFailed,
            additionalBufferFeatures: allBufferFeatures,
          };
        }
      }
    } catch (err) {
      // Fallback: use the first field boundary for visualization
      const fallbackBuffer = turf.clone(fieldBoundaryGeoJSON.features[0]);
      fallbackBuffer.properties = { type: 'fieldBuffer' };
      bufferFeature = fallbackBuffer;
    }
  } else {
    bufferFailed = true;
  }

  return { usableArea, bufferFeature, bufferFailed };
}
